import { FunctionComponent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import Spinner from 'components/UI/Spinner';
import TransportationIsEmpty from '../List/TransportationIsEmpty';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { TransportationForm } from '../Form/TransportationForm';
import TransportationServicesTable from '../List/TransportationServicesTable';
import { Service, isTransportationService } from 'model/Service';
import { useAuth, useTransportationServices, DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import styles from './TransportationServices.module.scss';
import { resetServicesThunk } from 'actions/services/ServicesActions';
import Button from 'components/UI/V3/Button/Button';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';

const TransportationServices: FunctionComponent = () => {
    const {
        services: transportationServices,
        savedService,
        meta,
        fetchTransportationServices,
        duplicateTransportationService,
        toggleTransportationServiceStatus
    } = useTransportationServices();

    const dispatch = useDispatch();
    const auth = useAuth();
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const [loading, setLoading] = useState({ services: false, initialLoading: true });
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() =>
        auth.marketplaceId ? [auth.marketplaceId] : []
    );
    const [pageInfo, setPageInfo] = useState({
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [showAddTransportation, setShowAddTransportation] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [lastSavedServiceId, setLastSavedServiceId] = useState<number | undefined>();
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const fetchServices = () => {
        setLoading(prev => ({ ...prev, services: true }));
        fetchTransportationServices(pageInfo.page, pageInfo.pageSize, selectedOption, searchText, marketplaceIds);
    };

    useEffect(() => {
        dispatch(resetServicesThunk());
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!loading.initialLoading) {
            if (isFirstLoad) {
                setIsFirstLoad(false);
            }
            fetchServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageInfo, selectedOption, searchText, marketplaceIds]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.FetchSuccess:
            case ServicesStatus.Error:
                setLoading(prev => ({ ...prev, services: false, initialLoading: false }));
                break;
            case ServicesStatus.SaveBulkSuccess:
                fetchServices();
                break;
        }
    }, [servicesStatus]);

    useEffect(() => {
        if (savedService && isTransportationService(savedService)) {
            if (savedService.id && savedService.id !== lastSavedServiceId) {
                setLastSavedServiceId(savedService.id);
                if (isFirstLoad) {
                    setIsFirstLoad(false);
                }
                fetchServices();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedService]);

    const handlePageChange = (page: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (size: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo({ page: 1, pageSize: size });
    };

    const handleStatusChange = (option: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSelectedOption(option);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (text: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleMarketplaceIdsChange = (ids: Array<number>) => {
        setLoading(prev => ({ ...prev, services: true }));
        setMarketplaceIds(ids);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleEditService = (service: Service) => {
        setEditingService(service);
        setShowAddTransportation(true);
    };

    const handleDuplicateService = (service: Service) => {
        duplicateTransportationService(service);
    };

    const handleDeactivateService = (service: Service) => {
        toggleTransportationServiceStatus(service, {
            page: pageInfo.page,
            per: pageInfo.pageSize,
            status: selectedOption,
            searchText: searchText,
            marketplaceIds
        });
    };

    const handleCloseModal = () => {
        setShowAddTransportation(false);
        setEditingService(null);
    };

    const headerRightContent = (
        <div className={styles.headerRightContent}>
            <OrganizationMarketplacesFilter
                selected={marketplaceIds}
                singleSelection
                onChangeSelection={handleMarketplaceIdsChange}
                className={styles.locationsFilter}
            />
            <Button
                leftIcon={faPlus}
                label={isMobile ? 'Add' : 'Add Service'}
                onClick={() => setShowAddTransportation(true)}
                id="transportation_add_button"
            />
        </div>
    );

    const renderContent = () => {
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        if (
            isFirstLoad &&
            transportationServices.length === 0 &&
            meta?.totalServices === 0 &&
            selectedOption === 'active' &&
            !searchText &&
            !loading.services
        ) {
            return (
                <div className={styles.root}>
                    <TransportationIsEmpty
                        onClick={() => setShowAddTransportation(true)}
                        headerRightContent={headerRightContent}
                    />
                </div>
            );
        }

        return (
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Transportation Services'}
                        buttonLabel={isMobile ? 'Add' : 'Add Service'}
                        displayButton={true}
                        onClick={() => setShowAddTransportation(true)}
                        id="transportation_add_button"
                        className={styles.headerContainer}
                        customButton={headerRightContent}
                    />

                    <SelectionBar
                        options={[
                            { id: 'active', name: 'Active' },
                            { id: 'inactive', name: 'Inactive' },
                            { id: 'all', name: 'All' }
                        ]}
                        selectedId={selectedOption}
                        onSelect={handleStatusChange}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by service name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <div className={styles.tableContainer}>
                    <TransportationServicesTable
                        services={transportationServices}
                        meta={meta}
                        isLoading={loading.services}
                        isEmpty={transportationServices.length === 0 && !loading.services}
                        onEdit={handleEditService}
                        onDuplicate={handleDuplicateService}
                        onDeactivate={handleDeactivateService}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onRetry={() => fetchServices()}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showAddTransportation && (
                <TransportationForm
                    mode={editingService ? 'edit' : 'add'}
                    service={editingService || undefined}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default TransportationServices;
